import React, { useState, useEffect } from 'react';
import {
  Modal,
  Header,
  Button,
  Message,
  Card,
  Icon,
  Label,
  Divider,
  Grid,
  Segment,
  List,
  Tab,
  Form,
  TextArea,
  Dropdown
} from 'semantic-ui-react';

/**
 * MessageVerificationModal - Shows sent message verification and recipient perspective
 * Provides comprehensive message testing verification with delivery status and recipient simulation
 */
const MessageVerificationModal = ({
  isOpen,
  onClose,
  messageData,
  sender,
  recipient,
  deliveryStatus = 'sent',
  onSimulateNotification,
  businessMessageConfig = {},
  useRealData = false,
  onMessageAction = null,
  availableUsers = {}
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [notificationSimulated, setNotificationSimulated] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [forwardRecipient, setForwardRecipient] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [messageArchived, setMessageArchived] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentTab(0);
      setNotificationSimulated(false);
      setShowReplyModal(false);
      setShowForwardModal(false);
      setReplyContent('');
      setForwardRecipient('');
      setForwardNote('');
      setMessageArchived(false);
      setActionInProgress(false);
    }
  }, [isOpen]);

  if (!messageData || !sender || !recipient) {
    return null;
  }

  // Get message type configuration
  const messageConfig = businessMessageConfig[messageData.messageType] || {
    name: messageData.messageType,
    icon: 'mail'
  };

  // Simulate notification for recipient
  const handleSimulateNotification = () => {
    setNotificationSimulated(true);
    if (onSimulateNotification) {
      onSimulateNotification(messageData, recipient);
    }
  };

  // Handle Reply action
  const handleReply = () => {
    if (!useRealData) {
      alert('Reply functionality is disabled in Mock mode. Switch to Real Data mode to test message actions.');
      return;
    }
    setShowReplyModal(true);
  };

  // Handle Forward action
  const handleForward = () => {
    if (!useRealData) {
      alert('Forward functionality is disabled in Mock mode. Switch to Real Data mode to test message actions.');
      return;
    }
    setShowForwardModal(true);
  };

  // Handle Archive action
  const handleArchive = () => {
    if (!useRealData) {
      alert('Archive functionality is disabled in Mock mode. Switch to Real Data mode to test message actions.');
      return;
    }

    setActionInProgress(true);
    setMessageArchived(true);

    // Simulate archive action
    setTimeout(() => {
      setActionInProgress(false);
      if (onMessageAction) {
        onMessageAction('archive', {
          messageId: messageData.id,
          recipientId: recipient.userId,
          action: 'archive'
        });
      }
    }, 1000);
  };

  // Send Reply
  const sendReply = () => {
    if (!replyContent.trim()) return;

    setActionInProgress(true);

    const replyMessage = {
      id: `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      senderId: recipient.userId,
      recipientId: sender.userId,
      content: replyContent,
      messageType: 'GENERAL', // Reply as general message
      senderRole: recipient.role,
      metadata: {
        testMessage: true,
        timestamp: new Date().toISOString(),
        replyTo: messageData.id,
        originalMessageType: messageData.messageType
      }
    };

    setTimeout(() => {
      setActionInProgress(false);
      setShowReplyModal(false);
      setReplyContent('');

      if (onMessageAction) {
        onMessageAction('reply', replyMessage);
      }
    }, 1000);
  };

  // Send Forward
  const sendForward = () => {
    if (!forwardRecipient || !forwardNote.trim()) return;

    setActionInProgress(true);

    const forwardMessage = {
      id: `forward-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      senderId: recipient.userId,
      recipientId: forwardRecipient,
      content: `${forwardNote}\n\n--- Forwarded Message ---\nFrom: ${sender.name}\nTo: ${recipient.name}\nSubject: ${messageConfig.name}\n\n${messageData.content}`,
      messageType: 'GENERAL',
      senderRole: recipient.role,
      metadata: {
        testMessage: true,
        timestamp: new Date().toISOString(),
        forwardedFrom: messageData.id,
        originalSender: sender.userId,
        originalMessageType: messageData.messageType
      }
    };

    setTimeout(() => {
      setActionInProgress(false);
      setShowForwardModal(false);
      setForwardRecipient('');
      setForwardNote('');

      if (onMessageAction) {
        onMessageAction('forward', forwardMessage);
      }
    }, 1000);
  };

  // Get available users for forwarding (exclude sender and recipient)
  const getForwardOptions = () => {
    const allUsers = availableUsers.all || availableUsers;
    return Object.entries(allUsers)
      .filter(([key, user]) =>
        user.userId !== sender.userId &&
        user.userId !== recipient.userId &&
        user.name && user.role
      )
      .map(([key, user]) => ({
        key: user.userId,
        value: user.userId,
        text: `${user.name} (${user.role})`,
        icon: 'user'
      }));
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Get delivery status color and icon
  const getDeliveryStatusDisplay = () => {
    switch (deliveryStatus) {
      case 'sent':
        return { color: 'blue', icon: 'paper plane', text: 'Sent' };
      case 'delivered':
        return { color: 'green', icon: 'check circle', text: 'Delivered' };
      case 'read':
        return { color: 'teal', icon: 'eye', text: 'Read' };
      case 'failed':
        return { color: 'red', icon: 'exclamation triangle', text: 'Failed' };
      default:
        return { color: 'grey', icon: 'clock', text: 'Pending' };
    }
  };

  const statusDisplay = getDeliveryStatusDisplay();

  // Render message content preview
  const renderMessagePreview = () => (
    <Card fluid>
      <Card.Content>
        <Card.Header>
          <Icon name={messageConfig.icon} />
          {messageConfig.name}
          <Label size="small" color={statusDisplay.color} floated="right">
            <Icon name={statusDisplay.icon} />
            {statusDisplay.text}
          </Label>
        </Card.Header>
        <Card.Meta>
          <strong>From:</strong> {sender.name} ({sender.role})
          <br />
          <strong>To:</strong> {recipient.name} ({recipient.role})
          <br />
          <strong>Sent:</strong> {formatTimestamp(messageData.metadata?.timestamp || new Date().toISOString())}
        </Card.Meta>
        <Divider />
        <Card.Description>
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            {messageData.content}
          </div>
        </Card.Description>
      </Card.Content>
      <Card.Content extra>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Icon name="info circle" />
            <small>Message ID: {messageData.id || 'test-message'}</small>
          </div>
          <div>
            {messageData.metadata?.generatedContent && (
              <Label size="mini" color="purple">
                <Icon name="magic" />
                Generated Content
              </Label>
            )}
            {messageData.metadata?.testMessage && (
              <Label size="mini" color="orange">
                <Icon name="lab" />
                Test Message
              </Label>
            )}
          </div>
        </div>
      </Card.Content>
    </Card>
  );

  // Render recipient perspective
  const renderRecipientPerspective = () => (
    <div>
      <Header as="h4">
        <Icon name="inbox" />
        {recipient.name}'s Inbox ({recipient.role})
        {!useRealData && (
          <Label size="mini" color="orange" style={{ marginLeft: '10px' }}>
            <Icon name="lab" />
            Mock Mode - Actions Disabled
          </Label>
        )}
      </Header>

      <Message info>
        <Message.Header>Recipient's Perspective</Message.Header>
        <Message.Content>
          This shows exactly how {recipient.name} would see and interact with this message in their inbox.
          {useRealData ? ' All actions are functional in Real Data mode.' : ' Actions are disabled in Mock mode.'}
        </Message.Content>
      </Message>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          <Icon name="bell" size="large" color="blue" />
          <div style={{ marginLeft: '10px' }}>
            <strong>New {messageConfig.name}</strong>
            <br />
            <small>From {sender.name}</small>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Button
              size="small"
              color="blue"
              onClick={handleSimulateNotification}
              disabled={notificationSimulated}
              icon={notificationSimulated ? 'check' : 'bell'}
              content={notificationSimulated ? 'Notification Sent' : 'Simulate Notification'}
            />
          </div>
        </div>

        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name="mail" />
              {messageConfig.name} - {recipient.name}'s Inbox
              {messageArchived ? (
                <Label size="mini" color="grey" floated="right">
                  <Icon name="archive" />
                  Archived
                </Label>
              ) : (
                <Label size="mini" color="red" floated="right">
                  <Icon name="circle" />
                  Unread
                </Label>
              )}
            </Card.Header>
            <Card.Meta>
              <Icon name="user" />
              From: {sender.name} ({sender.role})
              <span style={{ float: 'right' }}>
                <Icon name="clock" />
                {formatTimestamp(messageData.metadata?.timestamp || new Date().toISOString())}
              </span>
            </Card.Meta>
            <Card.Description>
              <div style={{
                padding: '10px',
                backgroundColor: '#fff',
                border: '1px solid #e1e8ed',
                borderRadius: '4px',
                marginTop: '10px'
              }}>
                <div style={{
                  fontSize: '0.9em',
                  color: '#666',
                  marginBottom: '10px'
                }}>
                  <Icon name={messageConfig.icon} />
                  {messageConfig.name} • Priority: Normal • To: {recipient.name}
                </div>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.4'
                }}>
                  {messageData.content}
                </div>
              </div>
            </Card.Description>
          </Card.Content>
          <Card.Content extra>
            <Button.Group size="small">
              <Button
                icon="reply"
                content="Reply"
                onClick={handleReply}
                disabled={messageArchived || actionInProgress}
                loading={actionInProgress && showReplyModal}
              />
              <Button
                icon="forward"
                content="Forward"
                onClick={handleForward}
                disabled={messageArchived || actionInProgress}
                loading={actionInProgress && showForwardModal}
              />
              <Button
                icon="archive"
                content={messageArchived ? "Archived" : "Archive"}
                onClick={handleArchive}
                disabled={messageArchived || actionInProgress}
                loading={actionInProgress && !showReplyModal && !showForwardModal}
                color={messageArchived ? "grey" : undefined}
              />
            </Button.Group>
            {!useRealData && (
              <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#666' }}>
                <Icon name="info circle" />
                Switch to Real Data mode to test Reply, Forward, and Archive functionality
              </div>
            )}
          </Card.Content>
        </Card>
      </div>

      {notificationSimulated && (
        <Message positive>
          <Message.Header>Notification Simulated</Message.Header>
          <Message.Content>
            {recipient.name} would receive a notification about this message through the Universal Message Icon
            and any configured notification channels.
          </Message.Content>
        </Message>
      )}

      {messageArchived && (
        <Message success>
          <Message.Header>Message Archived</Message.Header>
          <Message.Content>
            The message has been moved to {recipient.name}'s archived messages folder.
          </Message.Content>
        </Message>
      )}
    </div>
  );

  // Render technical details
  const renderTechnicalDetails = () => (
    <div>
      <Header as="h4">
        <Icon name="code" />
        Technical Details
      </Header>

      <Grid columns={2} divided>
        <Grid.Column>
          <Header as="h5">Message Metadata</Header>
          <List>
            <List.Item>
              <List.Icon name="tag" />
              <List.Content>
                <strong>Message Type:</strong> {messageData.messageType}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="user" />
              <List.Content>
                <strong>Sender ID:</strong> {messageData.senderId}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="users" />
              <List.Content>
                <strong>Recipient ID:</strong> {messageData.recipientId}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="shield" />
              <List.Content>
                <strong>Sender Role:</strong> {messageData.senderRole}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="clock" />
              <List.Content>
                <strong>Timestamp:</strong> {formatTimestamp(messageData.metadata?.timestamp || new Date().toISOString())}
              </List.Content>
            </List.Item>
          </List>
        </Grid.Column>

        <Grid.Column>
          <Header as="h5">Test Configuration</Header>
          <List>
            <List.Item>
              <List.Icon name="lab" />
              <List.Content>
                <strong>Test Message:</strong> {messageData.metadata?.testMessage ? 'Yes' : 'No'}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="magic" />
              <List.Content>
                <strong>Generated Content:</strong> {messageData.metadata?.generatedContent ? 'Yes' : 'No'}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="file" />
              <List.Content>
                <strong>Template Used:</strong> {messageData.metadata?.templateUsed || 'None'}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="briefcase" />
              <List.Content>
                <strong>Opportunity ID:</strong> {messageData.metadata?.opportunityId || 'None'}
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Icon name="chart line" />
              <List.Content>
                <strong>Delivery Status:</strong> {statusDisplay.text}
              </List.Content>
            </List.Item>
          </List>
        </Grid.Column>
      </Grid>

      <Divider />

      <Header as="h5">Message Content Analysis</Header>
      <Segment>
        <Grid columns={3} divided>
          <Grid.Column textAlign="center">
            <Header as="h6">Character Count</Header>
            <Label size="large" color="blue">
              {messageData.content.length}
            </Label>
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Header as="h6">Word Count</Header>
            <Label size="large" color="green">
              {messageData.content.split(/\s+/).length}
            </Label>
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Header as="h6">Line Count</Header>
            <Label size="large" color="orange">
              {messageData.content.split('\n').length}
            </Label>
          </Grid.Column>
        </Grid>
      </Segment>
    </div>
  );

  // Tab panes for the modal
  const panes = [
    {
      menuItem: { key: 'preview', icon: 'eye', content: 'Message Preview' },
      render: () => <Tab.Pane>{renderMessagePreview()}</Tab.Pane>
    },
    {
      menuItem: { key: 'recipient', icon: 'user', content: 'Recipient View' },
      render: () => <Tab.Pane>{renderRecipientPerspective()}</Tab.Pane>
    },
    {
      menuItem: { key: 'technical', icon: 'code', content: 'Technical Details' },
      render: () => <Tab.Pane>{renderTechnicalDetails()}</Tab.Pane>
    }
  ];

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        size="large"
        closeIcon
      >
        <Modal.Header>
          <Icon name="check circle" color="green" />
          Message Verification - {messageConfig.name}
          <Label color={statusDisplay.color} floated="right">
            <Icon name={statusDisplay.icon} />
            {statusDisplay.text}
          </Label>
        </Modal.Header>

        <Modal.Content scrolling>
          <Message success>
            <Message.Header>Message Sent Successfully</Message.Header>
            <Message.Content>
              Your {messageConfig.name.toLowerCase()} has been sent from <strong>{sender.name}</strong> to <strong>{recipient.name}</strong>.
              Use the tabs below to verify the message content and simulate the recipient experience.
            </Message.Content>
          </Message>

          <Tab
            panes={panes}
            activeIndex={currentTab}
            onTabChange={(e, { activeIndex }) => setCurrentTab(activeIndex)}
          />
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={handleSimulateNotification}
            disabled={notificationSimulated}
            color="blue"
            icon={notificationSimulated ? 'check' : 'bell'}
            content={notificationSimulated ? 'Notification Sent' : 'Simulate Notification'}
          />
          <Button
            onClick={onClose}
            primary
            icon="checkmark"
            content="Verification Complete"
          />
        </Modal.Actions>
      </Modal>

      {/* Reply Modal */}
      <Modal
        open={showReplyModal}
        onClose={() => setShowReplyModal(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="reply" />
          Reply to {sender.name}
        </Modal.Header>
        <Modal.Content>
          <Message info>
            <Message.Header>Replying as {recipient.name}</Message.Header>
            <Message.Content>
              Your reply will be sent from {recipient.name} ({recipient.role}) back to {sender.name} ({sender.role}).
            </Message.Content>
          </Message>

          <Form>
            <Form.Field>
              <label>Reply Message</label>
              <TextArea
                placeholder={`Write your reply as ${recipient.name}...`}
                value={replyContent}
                onChange={(e, { value }) => setReplyContent(value)}
                rows={6}
                style={{ minHeight: '150px' }}
              />
            </Form.Field>
          </Form>

          <Divider />

          <Header as="h5">Original Message</Header>
          <div>
            <div style={{
              maxHeight: '150px',
              overflowY: 'auto',
              fontSize: '0.9em',
              color: '#666',
              whiteSpace: 'pre-wrap'
            }}>
              <strong>From:</strong> {sender.name}<br />
              <strong>Subject:</strong> {messageConfig.name}<br />
              <br />
              {messageData.content.substring(0, 200)}
              {messageData.content.length > 200 && '...'}
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowReplyModal(false)}
            disabled={actionInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={sendReply}
            primary
            disabled={!replyContent.trim() || actionInProgress}
            loading={actionInProgress}
            icon="send"
            content="Send Reply"
          />
        </Modal.Actions>
      </Modal>

      {/* Forward Modal */}
      <Modal
        open={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="forward" />
          Forward Message
        </Modal.Header>
        <Modal.Content>
          <Message info>
            <Message.Header>Forwarding as {recipient.name}</Message.Header>
            <Message.Content>
              You are forwarding this message from {recipient.name} ({recipient.role}) to another user.
            </Message.Content>
          </Message>

          <Form>
            <Form.Field>
              <label>Forward To</label>
              <Dropdown
                placeholder="Select recipient..."
                fluid
                selection
                value={forwardRecipient}
                options={getForwardOptions()}
                onChange={(e, { value }) => setForwardRecipient(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Forward Note</label>
              <TextArea
                placeholder={`Add a note from ${recipient.name}...`}
                value={forwardNote}
                onChange={(e, { value }) => setForwardNote(value)}
                rows={3}
              />
            </Form.Field>
          </Form>

          <Divider />

          <Header as="h5">Message Being Forwarded</Header>
          <Segment>
            <div style={{
              maxHeight: '150px',
              overflowY: 'auto',
              fontSize: '0.9em',
              color: '#666',
              whiteSpace: 'pre-wrap'
            }}>
              <strong>Original From:</strong> {sender.name}<br />
              <strong>Original To:</strong> {recipient.name}<br />
              <strong>Subject:</strong> {messageConfig.name}<br />
              <br />
              {messageData.content.substring(0, 200)}
              {messageData.content.length > 200 && '...'}
            </div>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowForwardModal(false)}
            disabled={actionInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={sendForward}
            primary
            disabled={!forwardRecipient || !forwardNote.trim() || actionInProgress}
            loading={actionInProgress}
            icon="forward"
            content="Forward Message"
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default MessageVerificationModal;
